import { useState, useEffect } from 'react';
import Modal from '../../../Modals/Modal';
import styles from './ManageColumnsVisibilityModal.module.css';
import { useLanguage } from '../../../../context/LanguageContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIconPack } from "../../../../context/IconPackContext";

interface ManageColumnsVisibilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  columns: string[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
}

export default function ManageColumnsVisibilityModal({ 
  isOpen, 
  onClose, 
  columns,
  visibleColumns,
  onApply 
}: ManageColumnsVisibilityModalProps) {
  const { t } = useLanguage();
  const { getIcon } = useIconPack();
  const [selectedColumns, setSelectedColumns] = useState<string[]>(visibleColumns);

  useEffect(() => {
    if (isOpen) {
      setSelectedColumns(visibleColumns);
    }
  }, [isOpen, visibleColumns]);

  const handleToggle = (column: string) => {
    setSelectedColumns(prev => {
      const isSelected = prev.includes(column);

      if (isSelected && prev.length === 1) {
        return prev; 
      }

      return isSelected
        ? prev.filter(col => col !== column)
        : [...prev, column];
    });
  };

  const handleSelectAll = () => {
    setSelectedColumns([...columns]);
  };

  const handleDeselectAll = () => {
    if (columns.length > 0) {
      setSelectedColumns([columns[0]]);
    }
  };

  const handleApply = () => {
    if (selectedColumns.length === 0) {
      setSelectedColumns([columns[0]]);
      onApply([columns[0]]);
    } else {
      onApply(selectedColumns);
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedColumns(visibleColumns);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title={t('manageColumns')}>
      <div className={styles.container}>
        <div className={styles.columnsList}>
          {columns.map((column) => {
            const isSelected = selectedColumns.includes(column);
            const isLastSelected = selectedColumns.length === 1 && isSelected;
            
            return (
              <div 
                key={column} 
                className={`${styles.columnItem} ${isLastSelected ? styles.columnItemDisabled : ''}`}
                title={isLastSelected ? t('cannotDeselectLastColumn') : ''}
              >
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    className={styles.customCheckbox}
                    checked={isSelected}
                    onChange={() => handleToggle(column)}
                  />
                  <span className={styles.columnLabel}>
                    <span className={styles.columnName}>{column}</span>
                    <FontAwesomeIcon 
                      icon={getIcon(isSelected ? "eye" : "eye-slash")} 
                      className={isSelected ? styles.iconActive : styles.iconInactive} 
                    />
                  </span>
                </label>
              </div>
            );
          })}
        </div>

        <div className={styles.footer}>
          <div className={styles.actions}>
            <button 
              type="button" 
              className={styles.selectAllBtn}
              onClick={handleSelectAll}
              title={t('selectAll')}
            >
              <FontAwesomeIcon icon={getIcon("eye")} className={styles.buttonIcon} />
            </button>
            <button 
              type="button" 
              className={styles.deselectAllBtn}
              onClick={handleDeselectAll}
              title={t('deselectAll')}
            >
              <FontAwesomeIcon icon={getIcon("eye-slash")} className={styles.buttonIcon} />
            </button>
            <button 
              type="button" 
              className={styles.cancelBtn} 
              onClick={handleCancel}
            >
              {t('cancel')}
            </button>
            <button 
              type="button" 
              className={styles.applyBtn}
              onClick={handleApply}
            >
              {t('apply')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}